import { create } from 'zustand';
import type { AuthUser } from '../auth/cognito';

interface AuthState {
  user: AuthUser | undefined;
  idToken: string | undefined;
  accessToken: string | undefined;
  expiresAt: number | undefined;
  /** Single-session enforcement (FR-66). Sent on every API call as
   *  `X-Session-Id`. Established by /session/claim after sign-in. */
  sessionId: string | undefined;
  ready: boolean;
  /** Set when the Twilio token refresh has terminally failed and the user
   *  must sign in again. Surfaced as a banner; RouteGuard treats user as
   *  unauthenticated when this is set after the in-flight call ends. */
  reauthRequired: boolean;
  /** Set when the server reports `session_revoked` — another device took
   *  the session or an admin force-kicked. Frontend signs out + shows a
   *  toast. */
  sessionRevoked: boolean;
  setSession: (s: { user: AuthUser; idToken: string; accessToken: string; expiresAt: number; sessionId?: string }) => void;
  setSessionId: (sessionId: string) => void;
  clear: () => void;
  markReady: () => void;
  setReauthRequired: (required: boolean) => void;
  setSessionRevoked: (revoked: boolean) => void;
}

export const useAuth = create<AuthState>(set => ({
  user: undefined,
  idToken: undefined,
  accessToken: undefined,
  expiresAt: undefined,
  sessionId: undefined,
  ready: false,
  reauthRequired: false,
  sessionRevoked: false,
  setSession: ({ user, idToken, accessToken, expiresAt, sessionId }) =>
    set({ user, idToken, accessToken, expiresAt, sessionId, ready: true, reauthRequired: false, sessionRevoked: false }),
  setSessionId: (sessionId) => set({ sessionId }),
  clear: () =>
    set({ user: undefined, idToken: undefined, accessToken: undefined, expiresAt: undefined, sessionId: undefined, ready: true, reauthRequired: false, sessionRevoked: false }),
  markReady: () => set({ ready: true }),
  setReauthRequired: (required) => set({ reauthRequired: required }),
  setSessionRevoked: (revoked) => set({ sessionRevoked: revoked }),
}));
